/**
 * 구버전 아이디 → 신 아이디 기본 변환. 계정 이관 스크립트(platform-api
 * `scripts/xcost-legacy-import/lib/login-id.mjs`)와 **같은 규칙**이다 — 예전 아이디를 그대로 쳐도 새 아이디로
 * 로그인되게 개인 로그인 화면이 입력을 이 규칙으로 바꿔 보낸다. 두 곳을 함께 고친다(같은 시험 값으로 검증).
 *
 *   1. 이메일형이면 `@` 부터 뒤를 버린다     abc.def@naver.com → abc.def
 *   2. 한글은 두벌식 자판의 영문 키로        테스트12 → xptmxm12
 *   3. 대문자 → 소문자
 *   4. `.` 과 그 밖의 허용 안 되는 문자 → `_`   abc.def → abc_def
 *   5. 20자로 자른다
 * 4자 미만·예약어 보정과 중복 회피(`_2`…)로 이 값과 달라진 계정은 서버가 원문(`legacyLoginId`)으로 찾는다.
 */

// 두벌식 자판: 초성 19 · 중성 21 · 종성 28(0 = 없음). 쌍자음·ㅒ·ㅖ 는 Shift 키라 대문자 — 3단계에서 소문자가 된다.
const CHO = 'r R s e E f a q Q t T d w W c z x v g'.split(' ');
const JUNG = 'k o i O j p u P h hk ho hl y n nj np nl b m ml l'.split(' ');
const JONG = [
  '', 'r', 'R', 'rt', 's', 'sw', 'sg', 'e', 'f', 'fr', 'fa', 'fq', 'ft', 'fx',
  'fv', 'fg', 'a', 'q', 'qt', 't', 'T', 'd', 'w', 'c', 'z', 'x', 'v', 'g',
];
// 호환 자모(ㄱ U+3131 ~ ㅣ U+3163) — 낱자로 입력된 경우
const COMPAT: Record<string, string> = {
  ㄱ: 'r', ㄲ: 'R', ㄳ: 'rt', ㄴ: 's', ㄵ: 'sw', ㄶ: 'sg', ㄷ: 'e', ㄸ: 'E', ㄹ: 'f',
  ㄺ: 'fr', ㄻ: 'fa', ㄼ: 'fq', ㄽ: 'ft', ㄾ: 'fx', ㄿ: 'fv', ㅀ: 'fg', ㅁ: 'a',
  ㅂ: 'q', ㅃ: 'Q', ㅄ: 'qt', ㅅ: 't', ㅆ: 'T', ㅇ: 'd', ㅈ: 'w', ㅉ: 'W', ㅊ: 'c',
  ㅋ: 'z', ㅌ: 'x', ㅍ: 'v', ㅎ: 'g', ㅏ: 'k', ㅐ: 'o', ㅑ: 'i', ㅒ: 'O', ㅓ: 'j',
  ㅔ: 'p', ㅕ: 'u', ㅖ: 'P', ㅗ: 'h', ㅘ: 'hk', ㅙ: 'ho', ㅚ: 'hl', ㅛ: 'y', ㅜ: 'n',
  ㅝ: 'nj', ㅞ: 'np', ㅟ: 'nl', ㅠ: 'b', ㅡ: 'm', ㅢ: 'ml', ㅣ: 'l',
};
const SYLLABLE_FIRST = 0xac00;
const SYLLABLE_LAST = 0xd7a3;
const JONG_COUNT = 28;
const JUNG_JONG_COUNT = 588; // 21 × 28
const LOGIN_ID_MAX = 20;

/** 한글 → 두벌식 자판 키 문자열. 한글이 아닌 글자는 그대로. */
export function hangulToKeys(text: string): string {
  let out = '';
  for (const ch of text) {
    const code = ch.codePointAt(0) ?? 0;
    if (code >= SYLLABLE_FIRST && code <= SYLLABLE_LAST) {
      const offset = code - SYLLABLE_FIRST;
      out +=
        CHO[Math.floor(offset / JUNG_JONG_COUNT)] +
        JUNG[Math.floor((offset % JUNG_JONG_COUNT) / JONG_COUNT)] +
        JONG[offset % JONG_COUNT];
    } else out += COMPAT[ch] ?? ch;
  }
  return out;
}

/** 구 아이디 → 신 아이디 기본형. 이미 신 규칙인 아이디는 그대로 나온다. */
export function legacyToLoginId(raw: string): string {
  const local = raw.trim().split('@')[0] ?? '';
  return hangulToKeys(local)
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '_')
    .slice(0, LOGIN_ID_MAX);
}
